import Circle from "@arcgis/core/geometry/Circle";
import * as affineTransformOperator from "@arcgis/core/geometry/operators/affineTransformOperator.js";
import * as geodeticLengthOperator from "@arcgis/core/geometry/operators/geodeticLengthOperator.js";
import * as lengthOperator from "@arcgis/core/geometry/operators/lengthOperator.js";
import Transformation from "@arcgis/core/geometry/operators/support/Transformation.js";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import LineSymbolMarker from "@arcgis/core/symbols/LineSymbolMarker";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import MapView from "@arcgis/core/views/MapView";
import { canUseGeodesic, computeTrueNorth } from "../../../utils/arcgisUtils";
import { Unit } from "./measurementEnums";

const createLine = (vertices: number[][], mapview: MapView) => {
    const polyline = new Polyline({
        paths: [vertices],
        spatialReference: mapview.spatialReference,
    });
    return polyline;
};

export const drawPeilelinjal = async (
    vertices: number[][],
    setCurrentMeasures: Function,
    mapview: MapView,
    unit: string | null
) => {
    let peilelinjalGraphicsLayer = mapview.map?.findLayerById("peilelinjalGraphicsLayer") as GraphicsLayer;
    if (!peilelinjalGraphicsLayer) {
        peilelinjalGraphicsLayer = new GraphicsLayer({
            id: "peilelinjalGraphicsLayer",
            title: "Peilelinjal",
            listMode: "hide",
        });
        mapview.map?.add(peilelinjalGraphicsLayer);
    }

    peilelinjalGraphicsLayer.removeAll();

    const line = createLine(vertices, mapview);

    let lineLengthMeters: number = 0;
    let lineLengthNauticalMiles: number = 0;

    if (canUseGeodesic(mapview)) {
        if (!geodeticLengthOperator.isLoaded()) {
            await geodeticLengthOperator.load();
        }
        lineLengthMeters = geodeticLengthOperator.execute(line, { unit: "meters" });
        lineLengthNauticalMiles = geodeticLengthOperator.execute(line, { unit: "nautical-miles" });
    } else {
        lineLengthMeters = lengthOperator.execute(line, { unit: "meters" });
        lineLengthNauticalMiles = lengthOperator.execute(line, { unit: "nautical-miles" });
    }

    const lineGraphic = new Graphic({
        geometry: line,
        symbol: new SimpleLineSymbol({
            color: [4, 90, 141],
            width: 2,
            style: "dash",
            marker: new LineSymbolMarker({
                style: "circle",
                placement: "begin-end",
                color: [4, 90, 141],
            }),
        }),
    });

    const circle = new Circle({
        center: new Point({
            x: vertices[0][0],
            y: vertices[0][1],
            spatialReference: mapview.spatialReference,
        }),
        radius: lineLengthMeters,
        radiusUnit: "meters",
        geodesic: canUseGeodesic(mapview),
    });
    const circleGraphic = new Graphic({
        geometry: circle,
        symbol: new SimpleLineSymbol({
            color: [4, 90, 141],
            width: 2,
        }),
    });

    const gridNorthLine = new Polyline({
        paths: [
            [
                [vertices[0][0], vertices[0][1]],
                [vertices[0][0], vertices[0][1] + lineLengthMeters],
            ],
        ],
        spatialReference: mapview.spatialReference,
    });

    const transformation = new Transformation();
    transformation.rotate(await computeTrueNorth(circle.center), circle.center?.x ?? 0, circle.center?.y ?? 0);
    const trueNorthLine = affineTransformOperator.execute(gridNorthLine, transformation);
    const northLineGraphic = new Graphic({
        geometry: trueNorthLine,
        symbol: new SimpleLineSymbol({
            color: [4, 90, 141],
            width: 2,
        }),
    });

    peilelinjalGraphicsLayer.addMany([lineGraphic, circleGraphic, northLineGraphic]);

    if (vertices.length < 2) return;

    const angleDrawnLine =
        Math.atan2(vertices[1][1] - vertices[0][1], vertices[1][0] - vertices[0][0]) * (180 / Math.PI);
    const angleTrueNorthLine =
        Math.atan2(
            (trueNorthLine as Polyline).paths[0][1][1] - (trueNorthLine as Polyline).paths[0][0][1],
            (trueNorthLine as Polyline).paths[0][1][0] - (trueNorthLine as Polyline).paths[0][0][0]
        ) *
        (180 / Math.PI);
    const angleBetweenLines = (angleTrueNorthLine - angleDrawnLine + 360) % 360;
    const xoffsetNorthLabel = Math.cos((angleTrueNorthLine / 180) * Math.PI) * 5;
    const yoffsetNorthLabel = Math.sin((angleTrueNorthLine / 180) * Math.PI) * 5;

    const counterClockwiseAngle = 360 - angleBetweenLines;

    const cwAngleText = `${angleBetweenLines.toFixed(1)}°`;
    const ccwAngleText = `${counterClockwiseAngle.toFixed(1)}°`;

    const textAngle = -Math.atan2(vertices[1][1] - vertices[0][1], vertices[1][0] - vertices[0][0]) * (180 / Math.PI);
    const xoffsetMeasurements = Math.sin((textAngle / 180) * Math.PI) * 12;
    const yoffsetMeasurements = Math.cos((textAngle / 180) * Math.PI) * 8;
    const angleBreakPointCondition = textAngle >= 90 || textAngle <= -90;
    const labelLengthMetersText =
        lineLengthMeters < 1000 ? `${lineLengthMeters.toFixed(2)} m` : `${(lineLengthMeters / 1000).toFixed(2)} km`;
    const labelLengthNauticalMilesText = `${lineLengthNauticalMiles.toFixed(2)} Nm`;

    const lengthText = unit === Unit.Meter ? labelLengthMetersText : labelLengthNauticalMilesText;

    const labelLengthSymbol = new TextSymbol({
        text: lengthText,
        color: "black",
        font: {
            size: 12,
            weight: "bold",
        },
        verticalAlignment: "bottom",
        horizontalAlignment: "center",
        rotated: true,
        angle: angleBreakPointCondition ? textAngle + 180 : textAngle,
        xoffset: angleBreakPointCondition ? -xoffsetMeasurements : xoffsetMeasurements,
        yoffset: angleBreakPointCondition ? -yoffsetMeasurements : yoffsetMeasurements,
    });

    const labelLengthPoint = new Point({
        x: (vertices[0][0] + vertices[1][0]) / 2,
        y: (vertices[0][1] + vertices[1][1]) / 2,
        spatialReference: mapview.spatialReference,
    });

    const labelLength = new Graphic({
        geometry: labelLengthPoint,
        symbol: labelLengthSymbol,
        attributes: {
            tool: "peilelinjal",
            type: "lengthLabel",
            lengthMeters: lineLengthMeters,
            lengthNauticalMiles: lineLengthNauticalMiles,
        },
    });

    const angleText = `${cwAngleText} / ${ccwAngleText}`;

    const labelAngleSymbol = new TextSymbol({
        text: angleText,
        color: "black",
        font: {
            size: 12,
            weight: "bold",
        },
        verticalAlignment: "top",
        horizontalAlignment: "center",
        rotated: true,
        angle: angleBreakPointCondition ? textAngle + 180 : textAngle,
        xoffset: angleBreakPointCondition ? xoffsetMeasurements : -xoffsetMeasurements,
        yoffset: angleBreakPointCondition ? yoffsetMeasurements : -yoffsetMeasurements,
    });

    const labelAngle = new Graphic({
        geometry: labelLengthPoint,
        symbol: labelAngleSymbol,
        attributes: {
            tool: "peilelinjal",
            type: "angleLabel",
            lengthMeters: lineLengthMeters,
            lengthNauticalMiles: lineLengthNauticalMiles,
            angleDegrees: angleBetweenLines,
            counterClockwiseAngleDegrees: counterClockwiseAngle,
        },
    });

    const labelNorthSymbol = new TextSymbol({
        text: "N",
        color: "black",
        font: {
            size: 12,
            weight: "bold",
        },
        verticalAlignment: "bottom",
        horizontalAlignment: "center",
        rotated: true,
        angle: -angleTrueNorthLine + 90,
        xoffset: xoffsetNorthLabel,
        yoffset: yoffsetNorthLabel,
    });

    const labelNorthPoint = new Point({
        x: (trueNorthLine as Polyline).paths[0][1][0],
        y: (trueNorthLine as Polyline).paths[0][1][1],
        spatialReference: mapview.spatialReference,
    });

    const labelNorth = new Graphic({
        geometry: labelNorthPoint,
        symbol: labelNorthSymbol,
        attributes: {
            tool: "peilelinjal",
            type: "northLabel",
            lengthMeters: lineLengthMeters,
            lengthNauticalMiles: lineLengthNauticalMiles,
        },
    });

    peilelinjalGraphicsLayer.addMany([labelLength, labelAngle, labelNorth]);

    setCurrentMeasures([
        { key: "Lengde", value: lengthText },
        { key: "Angle CW", value: cwAngleText },
        { key: "Angle CCW", value: ccwAngleText },
    ]);
};
